import { Spinner } from '@components/ui';
import { CookieRadarResult } from '@dieterApi/scan/types';
import { ScanResultData } from '@dieterApi/scan/useScanResultQuery';
import { translateScannerNote } from '@app/utils/translateScannerNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Collapse, IconButton, Tooltip } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MatchEvidence,
  SecurityHeadersData,
  SECURITY_HEADER_HELP_KEY,
  computeCoverage,
  parseDseMatches,
} from './scanResultShared';

const SCORE_STYLES: Record<string, { bg: string; text: string; labelKey: string }> = {
  A: { bg: 'bg-green-100', text: 'text-green-700', labelKey: 'route.scanresult.score_label.a' },
  B: { bg: 'bg-teal-100', text: 'text-teal-700', labelKey: 'route.scanresult.score_label.b' },
  C: { bg: 'bg-yellow-100', text: 'text-yellow-700', labelKey: 'route.scanresult.score_label.c' },
  D: { bg: 'bg-orange-100', text: 'text-orange-700', labelKey: 'route.scanresult.score_label.d' },
  E: { bg: 'bg-red-100', text: 'text-red-600', labelKey: 'route.scanresult.score_label.e' },
  F: { bg: 'bg-red-200', text: 'text-red-800', labelKey: 'route.scanresult.score_label.f' },
};

function ScoreBadge({ score, label }: { score: string; label: string }) {
  const style = SCORE_STYLES[score?.toUpperCase()] ?? { bg: 'bg-gray-100', text: 'text-gray-600', labelKey: '' };
  return (
    <div className={`flex flex-col items-center justify-center rounded-xl w-20 h-20 ${style.bg}`}>
      <span className={`text-4xl font-bold ${style.text}`}>{score?.toUpperCase() ?? '?'}</span>
      <span className={`text-xs font-medium ${style.text}`}>{label}</span>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide mb-2">{children}</h3>;
}

function Tag({ children, variant = 'neutral' }: { children: React.ReactNode; variant?: 'ok' | 'warn' | 'generic' | 'error' | 'neutral' }) {
  const cls =
    variant === 'ok'
      ? 'bg-green-50 text-green-700 border-green-200'
      : variant === 'generic'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : variant === 'warn'
          ? 'bg-yellow-50 text-orange-500 border-yellow-200'
          : variant === 'error'
            ? 'bg-red-50 text-red-700 border-red-200'
            : 'bg-gray-50 text-gray-600 border-gray-200';
  return <span className={`text-xs border rounded px-1.5 py-0.5 ${cls}`}>{children}</span>;
}

function ScanResultDisplayComponent({
  data,
  loading,
  startUrl,
  noScansLeft,
  timedOut,
  forceExpandAll,
}: {
  data?: ScanResultData;
  startUrl: string;
  loading: boolean;
  noScansLeft: boolean;
  timedOut?: boolean;
  forceExpandAll?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [consentDetailsOpen, setConsentDetailsOpen] = useState(!!forceExpandAll);
  const [securityDetailsOpen, setSecurityDetailsOpen] = useState(!!forceExpandAll);

  useEffect(() => {
    if (!forceExpandAll) return;
    setDetailsOpen(true);
    setConsentDetailsOpen(true);
    setSecurityDetailsOpen(true);
  }, [forceExpandAll]);

  const isFinished = data?.scanFinished;

  let results: CookieRadarResult | null = null;
  try {
    results = data?.results ? (JSON.parse(data.results) as CookieRadarResult) : null;
  } catch {
    results = null;
  }

  const normalizedStatus = results?.status?.toLowerCase() ?? '';
  const isFailed = (() => {
    try {
      return normalizedStatus === 'failed' || normalizedStatus === 'cancelled';
    } catch { return false; }
  })();
  const isQueued = normalizedStatus === 'queued';
  const queuePosition = typeof results?.queue?.position === 'number' ? results.queue.position : null;
  const staleRecovered = !!results?.local_meta?.stale_recovery;

  const site = results?.site;
  const states = results?.states;
  const run = results?.run;
  const httpsOk = (site as { https_ok?: boolean } | undefined)?.https_ok;
  const score = site?.privacy_score?.toUpperCase() ?? null;
  const scoreStyle = score ? SCORE_STYLES[score] : undefined;
  const scoreLabel = scoreStyle?.labelKey ? t(scoreStyle.labelKey) : t('route.scanresult.score_label.unknown');
  const discoveredDseUrl = ((site as { dse_found_url?: string } | undefined)?.dse_found_url ?? '').trim();
  const providedDseUrlRaw = (data?.dpsUrl ?? '').trim();
  const startUrlRaw = (data?.startUrl ?? startUrl ?? '').trim();
  const userProvidedDseUrl = providedDseUrlRaw && providedDseUrlRaw !== startUrlRaw ? providedDseUrlRaw : '';

  // Parse dse_matches safely (backend may return object or JSON string)
  const dseMatches = parseDseMatches((site as { dse_matches?: unknown } | undefined)?.dse_matches);

  // Whether a privacy policy was actually found (either user-provided or auto-discovered)
  const dseFound = !!(userProvidedDseUrl || discoveredDseUrl);

  const {
    dseCookieNames,
    genericCoveredCookieNames,
    technicallyNecessaryCookieNames,
    technicallyNecessaryTpDomains,
    dseTpDomains,
    allCookieNames,
    allTpDomains,
    cookiesNotInDse,
    tpsNotInDse,
    totalDeductions,
  } = computeCoverage(dseMatches, states);
  const cookieEvidenceByName = new Map(
    (dseMatches.cookie_evidence ?? [])
      .filter((e) => !!e?.name)
      .map((e) => [e.name.toLowerCase(), e] as const),
  );
  const tpEvidenceByDomain = new Map(
    (dseMatches.tp_evidence ?? [])
      .filter((e) => !!e?.domain)
      .map((e) => [e.domain.toLowerCase(), e] as const),
  );

  const getCookieProvider = (cookieName: string) => {
    const providerFromMap = dseMatches.cookie_providers[cookieName.toLowerCase()];
    if (providerFromMap) return providerFromMap;
    const evidence = cookieEvidenceByName.get(cookieName.toLowerCase());
    if (evidence?.source === 'vendor_db' && evidence.matched_by) return evidence.matched_by;
    return t('route.scanresult.details.tooltip_unknown_provider');
  };

  const getTpProvider = (domain: string) => {
    const providerFromMap = dseMatches.tp_providers[domain.toLowerCase()];
    if (providerFromMap) return providerFromMap;
    const evidence = tpEvidenceByDomain.get(domain.toLowerCase());
    if (evidence?.source === 'vendor_db' && evidence.matched_by) return evidence.matched_by;
    return t('route.scanresult.details.tooltip_unknown_provider');
  };

  const getDseKeyword = (evidence?: MatchEvidence) => {
    const raw = evidence?.matched_by?.trim();
    return raw ? raw : t('route.scanresult.details.tooltip_no_keyword');
  };

  const renderCoverageTooltip = (provider: string, keyword: string) => (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs"><strong>{t('route.scanresult.details.tooltip_provider')}:</strong> {provider}</span>
      <span className="text-xs"><strong>{t('route.scanresult.details.tooltip_dse_keyword')}:</strong> {keyword}</span>
    </div>
  );

  const scoreDeductionCookieNames = new Set(cookiesNotInDse);
  const securityHeadersData: SecurityHeadersData | null = dseMatches.security_headers ?? null;
  const securityHeadersGrade = securityHeadersData?.grade?.toUpperCase() ?? null;
  const scanProfile = dseMatches.scan_profile;
  const scanDepthLabel = scanProfile?.depth ? t(`route.scanresult.details.scan_depth_${scanProfile.depth}`) : null;
  const progressSiteLabel = (run?.progress_site ?? '').trim();

  // Progress is available in the `run` field (already parsed above)
  // Only show progress if scan is actively loading (not finished)
  const progressCurrent: number | null = (!isFinished || loading) ? (run?.progress_current ?? null) : null;
  const progressTotal: number | null = (!isFinished || loading) ? (run?.progress_total ?? null) : null;
  const showDeterminateProgress =
    (!isFinished || loading) &&
    progressCurrent != null && progressTotal != null && progressTotal > 0 && progressCurrent >= 1;
  const progressPercent = showDeterminateProgress
    ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100))
    : null;

  // Loading / timed-out state
  if (!isFinished || loading) {
    return (
      <div className="flex flex-col gap-4 items-center py-6">
        {timedOut && (
          <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-center">
            {t('route.scanresult.timeout.line1')}
            <br />
            {t('route.scanresult.timeout.line2')}
          </p>
        )}

        <Spinner size="large" />
        <span className="text-gray-700 font-medium text-center">
          {t('route.scanresult.loading')}
        </span>

        {(() => {
          const isPhase3 = progressSiteLabel.startsWith('Phase 3');
          if (showDeterminateProgress && !isPhase3) {
            return (
              <div className="flex flex-col items-center gap-1.5 w-full max-w-xs">
                <span className="text-sm text-gray-600">
                  {progressCurrent} / {progressTotal}
                  {progressPercent !== null && (
                    <span className="ml-1 text-gray-400">({progressPercent}%)</span>
                  )}
                </span>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent ?? 0}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 text-center">{progressSiteLabel}</span>
              </div>
            );
          }
          return (
            <span className="text-xs text-gray-500 text-center">
              {isQueued
                ? t('route.scanresult.progress_queued', { position: queuePosition ?? '?' })
                : progressSiteLabel || t('route.scanresult.progress_waiting')}
            </span>
          );
        })()}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* ── Summary row ── */}
      <div className="hidden flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        {score && <ScoreBadge score={score} label={scoreLabel} />}

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="font-medium text-gray-800 truncate">{site?.url ?? startUrl ?? data?.startUrl}</span>
          {run?.finished_at && (
            <span className={`text-xs ${run.status === 'failed' || run.status === 'cancelled' || run.error_msg ? 'text-red-500' : 'text-green-600'}`}>
              {run.status === 'failed' || run.status === 'cancelled' || run.error_msg
                ? t('route.scanresult.status.failed')
                : t('route.scanresult.status.completed')}
              : {new Date(run.finished_at).toLocaleString(i18n.resolvedLanguage || i18n.language || undefined)}
            </span>
          )}
          <div className="flex flex-wrap gap-2 mt-1">
            {isFailed ? (
              <Tag variant="error">
                {t('route.scanresult.failed_tag')}
              </Tag>
            ) : (
              <>
                <Tag variant={cookiesNotInDse.length === 0 ? 'ok' : 'warn'}>
                  {t('route.scanresult.tags.cookies_found', { count: allCookieNames.size })}
                </Tag>
                <Tag variant={tpsNotInDse.length === 0 ? 'ok' : 'warn'}>
                  {t('route.scanresult.tags.third_parties', { count: allTpDomains.size })}
                </Tag>
              </>)}
            {/* {run?.total_pages != null && <Tag>{run.total_pages} Seiten gescannt</Tag>} */}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Tooltip
            title={detailsOpen ? t('route.scanresult.tooltip.hide_details') : t('route.scanresult.tooltip.show_details')}
            followCursor
          >
            <IconButton size="small" onClick={() => setDetailsOpen((o) => !o)}>
              {detailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* ── Details panel ── */}
      <Collapse in={detailsOpen}>
        <div className="flex flex-col gap-6 p-4 bg-gray-50 rounded-xl border border-gray-200">

          {isFailed && staleRecovered && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs">
              {t('route.scanresult.stale_recovery_notice')}
            </div>
          )}

          {/* Score deduction breakdown */}
          <div className="scan-print-keep-together bg-white rounded-lg border border-gray-200 p-3">
            <p className="text-xs font-medium text-gray-500 mb-2">{t('route.scanresult.details.score_deduction_summary')}</p>
            {!dseFound ? (
              <div className="flex flex-col gap-1">
                {dseMatches.site_unreachable ? (
                  <p className="text-xs text-red-700 font-medium">{t('route.scanresult.details.site_unreachable_notice')}</p>
                ) : (
                  <>
                    <p className="text-xs text-red-700 font-medium">{t('route.scanresult.details.no_dse_score_notice')}</p>
                    <p className="text-xs text-gray-500">{t('route.scanresult.details.no_dse_www_tip')}</p>
                  </>
                )}
              </div>
            ) : totalDeductions === 0 ? (
              <p className="text-xs text-green-700">{t('route.scanresult.details.no_score_deduction')}</p>
            ) : (
              <div className="flex flex-col gap-3 text-xs">
                {cookiesNotInDse.length > 0 && (
                  <div>
                    <p className="text-red-600 font-medium mb-1">{t('route.scanresult.details.deduction_cookies_missing', { count: cookiesNotInDse.length })}</p>
                    <div className="flex flex-wrap gap-1">
                      {cookiesNotInDse.map((c) => (
                        <Tag key={c} variant="error">{c}</Tag>
                      ))}
                    </div>
                  </div>
                )}
                {tpsNotInDse.length > 0 && (
                  <div>
                    <p className="text-red-600 font-medium mb-1">{t('route.scanresult.details.deduction_tps_missing', { count: tpsNotInDse.length })}</p>
                    <div className="flex flex-wrap gap-1">
                      {tpsNotInDse.map((d) => (
                        <Tag key={d} variant="error">{d}</Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {scanProfile && (
            <div className="scan-print-keep-together bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs font-medium text-gray-500 mb-2">{t('route.scanresult.details.scan_profile')}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {scanDepthLabel && (
                  <Tag variant="neutral">{t('route.scanresult.details.scan_depth_label')}: {scanDepthLabel}</Tag>
                )}
                {typeof scanProfile.scanned_pages === 'number' && (
                  <Tag variant="neutral">{t('route.scanresult.details.scan_pages_label', { count: scanProfile.scanned_pages })}</Tag>
                )}
                {scanProfile.dse_source && (
                  <Tag variant="neutral">{t(`route.scanresult.details.scan_dse_source_${scanProfile.dse_source}`)}</Tag>
                )}
              </div>
            </div>
          )}

          {/* DSE Coverage */}
          <div className="scan-print-section">
            <SectionHeading>{t('route.scanresult.details.transport_encryption')}</SectionHeading>
            <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
              {securityHeadersData ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-gray-600">{t('route.scanresult.details.security_headers_summary')}</span>
                    <div className="flex items-center gap-2">
                      <Tag variant={securityHeadersData.https ? 'ok' : 'error'}>
                        {securityHeadersData.https ? t('route.scanresult.details.https_ok') : t('route.scanresult.details.https_bad')}
                      </Tag>
                      <Tag variant={securityHeadersGrade === 'A' || securityHeadersGrade === 'B' ? 'ok' : securityHeadersGrade === 'C' ? 'warn' : 'error'}>
                        {t('route.scanresult.details.security_grade', { grade: securityHeadersGrade || '?' })}
                      </Tag>
                      {!forceExpandAll && (
                        <IconButton size="small" onClick={() => setSecurityDetailsOpen((open) => !open)}>
                          {securityDetailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </div>
                  </div>

                  <Collapse in={securityDetailsOpen}>
                    <div className="flex flex-col gap-3 mt-2">
                      {(securityHeadersData.headers ?? []).map((hdr) => {
                        const isOk = hdr.rating === 'ok';
                        const isWarn = hdr.rating === 'warn';
                        const isBad = hdr.rating === 'bad';
                        const bgColor = isOk ? 'bg-green-50' : isWarn ? 'bg-amber-50' : 'bg-red-50';
                        const borderColor = isOk ? 'border-green-200' : isWarn ? 'border-amber-300' : 'border-red-200';
                        const headerTextColor = isOk ? 'text-green-900' : isWarn ? 'text-amber-900' : 'text-red-900';
                        const iconColor = isOk ? 'text-green-600' : isWarn ? 'text-amber-600' : 'text-red-600';
                        const statusIcon = isOk ? (
                          <CheckCircleIcon className={`${iconColor} flex-shrink-0`} fontSize="small" />
                        ) : isWarn ? (
                          <WarningAmberIcon className={`${iconColor} flex-shrink-0`} fontSize="small" />
                        ) : (
                          <ErrorOutlineIcon className={`${iconColor} flex-shrink-0`} fontSize="small" />
                        );

                        return (
                          <div key={hdr.name} className={`rounded-lg border-2 ${borderColor} ${bgColor} p-3 transition-all hover:shadow-sm`}>
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-start gap-2 min-w-0 flex-1">
                                <div className="mt-0.5 flex-shrink-0">{statusIcon}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className={`font-semibold text-sm ${headerTextColor}`}>{hdr.name}</h4>
                                    {SECURITY_HEADER_HELP_KEY[hdr.name] && (
                                      <Tooltip
                                        placement="top"
                                        arrow
                                        title={(
                                          <div className="text-xs max-w-xs">
                                            <p className="font-medium mb-1">
                                              {t(`route.scanresult.details.security_header_help.${SECURITY_HEADER_HELP_KEY[hdr.name]}.title`)}
                                            </p>
                                            <p>
                                              {t(`route.scanresult.details.security_header_help.${SECURITY_HEADER_HELP_KEY[hdr.name]}.description`)}
                                            </p>
                                          </div>
                                        )}
                                      >
                                        <InfoOutlinedIcon fontSize="small" className="text-gray-500 flex-shrink-0" />
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                <Tag variant={isOk ? 'ok' : isWarn ? 'warn' : 'error'}>
                                  {isOk
                                    ? t('route.scanresult.details.security_ok')
                                    : isWarn
                                      ? t('route.scanresult.details.security_warn')
                                      : t('route.scanresult.details.security_bad')}
                                </Tag>
                              </div>
                            </div>

                            {(hdr.notes?.length > 0 || hdr.value) && (
                              <div className="space-y-2">
                                {hdr.notes?.length > 0 && (
                                  <div className={`text-xs space-y-1.5 ${isOk ? 'text-green-700' : isWarn ? 'text-amber-700' : 'text-red-700'}`}>
                                    {hdr.notes.map((note, idx) => (
                                      <div key={idx} className="flex gap-2">
                                        <span className="flex-shrink-0 mt-0.5">•</span>
                                        <span>{translateScannerNote(note, i18n.resolvedLanguage || i18n.language)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {hdr.value && (
                                  <div className="mt-2 pt-2 border-t border-opacity-30" style={{
                                    borderTopColor: isOk ? '#dbeafe' : isWarn ? '#fef08a' : '#fee2e2'
                                  }}>
                                    <p className="text-[11px] text-gray-600 font-mono bg-gray-50 rounded p-2 break-all overflow-x-auto">
                                      {hdr.value}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {!hdr.value && !hdr.notes?.length && (
                              <p className={`text-[11px] font-medium ${isOk ? 'text-green-600' : isWarn ? 'text-amber-600' : 'text-red-600'}`}>
                                {t('route.scanresult.details.security_missing_header')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Collapse>
                </div>
              ) : typeof httpsOk === 'boolean' ? (
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-gray-600">{t('route.scanresult.details.website_https_reachable')}</span>
                  <Tag variant={httpsOk ? 'ok' : 'error'}>{httpsOk ? t('common.yes') : t('common.no')}</Tag>
                </div>
              ) : (
                <p className="text-xs text-gray-500">{t('route.scanresult.details.tls_unavailable')}</p>
              )}
            </div>

            <SectionHeading>{t('route.scanresult.details.privacy_policy_url')}</SectionHeading>
            <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
              {userProvidedDseUrl || discoveredDseUrl ? (
                <div className="flex flex-col gap-2">
                  {userProvidedDseUrl && (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-gray-500 font-medium">{t('route.scanresult.details.user_provided')}</p>
                      <a
                        href={userProvidedDseUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline break-all"
                      >
                        {userProvidedDseUrl}
                      </a>
                    </div>
                  )}
                  {discoveredDseUrl && (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-gray-500 font-medium">{t('route.scanresult.details.auto_discovered')}</p>
                      <a
                        href={discoveredDseUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline break-all"
                      >
                        {discoveredDseUrl}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  {t('route.scanresult.details.no_dse_url')}
                </p>
              )}
            </div>

            {/* Found Legal Pages - removed */}

            <SectionHeading>{t('route.scanresult.details.privacy_policy_coverage')}</SectionHeading>
            {isFailed ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-700 font-medium">
                  {t('route.scanresult.details.scan_failed_incomplete')}
                </p>
              </div>
            ) : !dseFound ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-600 font-medium">
                  {t('route.scanresult.details.no_dse_found')}
                </p>
              </div>
            ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-2 font-medium">{t('route.scanresult.details.cookies_all_states')}</p>
                {allCookieNames.size === 0 ? (
                  <p className="text-xs text-gray-400 italic">{t('route.scanresult.details.no_cookies_found')}</p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {[...allCookieNames].sort().map((cookieName) => {
                      const inDse = dseCookieNames.has(cookieName);
                      const genericCovered = !inDse && genericCoveredCookieNames.has(cookieName);
                      const isTechnicallyNecessary = technicallyNecessaryCookieNames.has(cookieName);
                      const showMissingInDse = !inDse && !genericCovered && !isTechnicallyNecessary;
                      return (
                        <li key={cookieName} className="flex items-center justify-between gap-2 text-xs">
                          <Tooltip title={renderCoverageTooltip(getCookieProvider(cookieName), getDseKeyword(cookieEvidenceByName.get(cookieName)))} placement="top" arrow>
                            <span className="font-mono text-gray-700 truncate cursor-help">{cookieName}</span>
                          </Tooltip>
                          <div className="flex gap-1 shrink-0">
                            {(inDse || genericCovered || showMissingInDse) && (
                              <Tag variant={inDse ? 'ok' : genericCovered ? 'generic' : 'error'}>
                                {inDse
                                  ? t('route.scanresult.details.in_dse')
                                  : genericCovered
                                    ? t('route.scanresult.details.generically_covered')
                                    : t('route.scanresult.details.missing_in_dse')}
                              </Tag>
                            )}
                            {isTechnicallyNecessary && (
                              <Tooltip title={t('route.scanresult.details.technically_necessary_tooltip')}>
                                <div>
                                  <Tag variant='neutral'>
                                    {t('route.scanresult.details.technically_necessary')}
                                  </Tag>
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Third parties */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500 mb-2 font-medium">{t('route.scanresult.details.third_parties')}</p>
                {allTpDomains.size === 0 ? (
                  <p className="text-xs text-gray-400 italic">{t('route.scanresult.details.no_third_parties_found')}</p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {[...allTpDomains].map((domain) => {
                      const inDse = dseTpDomains.has(domain);
                      const isTechNecessary = technicallyNecessaryTpDomains.has(domain);
                      return (
                        <li key={domain} className="flex items-center justify-between gap-2 text-xs">
                          <Tooltip title={renderCoverageTooltip(getTpProvider(domain), getDseKeyword(tpEvidenceByDomain.get(domain)))} placement="top" arrow>
                            <span className="font-mono text-gray-700 truncate cursor-help">{domain}</span>
                          </Tooltip>
                          <div className="flex gap-1 shrink-0">
                            {(inDse || !isTechNecessary) && (
                              <Tag variant={inDse ? 'ok' : 'error'}>
                                {inDse ? t('route.scanresult.details.in_dse') : t('route.scanresult.details.missing_in_dse')}
                              </Tag>
                            )}
                            {isTechNecessary && (
                              <Tooltip title={t('route.scanresult.details.technically_necessary_tooltip')}>
                                <div><Tag variant="neutral">{t('route.scanresult.details.technically_necessary')}</Tag></div>
                              </Tooltip>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {(cookiesNotInDse.length > 0 || tpsNotInDse.length > 0) && (
              cookiesNotInDse.length > 2 || tpsNotInDse.length > 2 ? (
                <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {t('route.scanresult.warning.many_missing_entries', { count: cookiesNotInDse.length + tpsNotInDse.length })}
                </p>
              ) : (
                <p className="mt-3 text-xs text-yelow-600 bg-orange-50 border border-yellow-200 rounded p-2">
                  {t('route.scanresult.warning.few_missing_entries', { count: cookiesNotInDse.length + tpsNotInDse.length })}
                </p>
              )
            )}
            </>
            )}
          </div>

          {/* Before / After consent comparison */}
          {states && (
            <div className="scan-print-page-break">
              <div className="flex items-center justify-between">
                <SectionHeading>{t('route.scanresult.details.before_after_consent')}</SectionHeading>
                {!forceExpandAll && (
                  <IconButton size="small" onClick={() => setConsentDetailsOpen((open) => !open)}>
                    {consentDetailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                )}
              </div>
              <Collapse in={consentDetailsOpen}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(['pre_consent', 'post_accept', 'post_reject'] as const).map((key) => {
                    const label =
                      key === 'pre_consent'
                        ? t('route.scanresult.details.consent_state.pre_consent')
                        : key === 'post_accept'
                          ? t('route.scanresult.details.consent_state.post_accept')
                          : t('route.scanresult.details.consent_state.post_reject');
                    const state = states[key];
                    return (
                      <div key={key} className="bg-white rounded-lg border border-gray-200 p-3">
                        <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>
                        <p className="text-xs text-gray-600 mb-1">
                          <span className="font-semibold">{state.cookies.length}</span>{' '}
                          {t('route.scanresult.details.cookies')}
                        </p>
                        {state.cookies.length > 0 && (
                          <ul className="text-xs text-gray-500 space-y-0.5">
                            {state.cookies.map((c) => {
                              const cookieNameLower = (c.name || '').toLowerCase();
                              const isScoreDeductionCookie =
                                key === 'post_reject' && scoreDeductionCookieNames.has(cookieNameLower);
                              return (
                                <li
                                  key={`${key}-${c.name}-${c.domain}`}
                                  className={`font-mono truncate ${isScoreDeductionCookie ? 'text-red-700' : ''}`}
                                >
                                  {c.name}
                                  {isScoreDeductionCookie && (
                                    <span className="ml-1 text-[10px] px-1 py-0.5 rounded border border-red-200 bg-red-50 text-red-700 align-middle">
                                      {t('route.scanresult.details.score_deduction')}
                                    </span>
                                  )}
                                  <span className="text-gray-400"> ({c.domain})</span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {(state.third_parties ?? []).length > 0 && (() => {
                          // The scanner emits one entry per (domain, resource_type) pair,
                          // so the same domain can appear multiple times. Deduplicate by
                          // domain, summing request_counts, before rendering.
                          const tpByDomain = new Map<string, { domain: string; totalRequests: number }>();
                          for (const tp of state.third_parties ?? []) {
                            const d = (tp.domain || '').toLowerCase();
                            if (!d) continue;
                            const existing = tpByDomain.get(d);
                            if (existing) {
                              existing.totalRequests += tp.request_count ?? 1;
                            } else {
                              tpByDomain.set(d, { domain: tp.domain, totalRequests: tp.request_count ?? 1 });
                            }
                          }
                          const uniqueTps = Array.from(tpByDomain.values());
                          return (
                          <>
                            <p className="text-xs text-gray-600 mt-2 mb-1 pt-2 border-t border-gray-100">
                              <span className="font-semibold">{uniqueTps.length}</span>{' '}
                              {t('route.scanresult.details.third_parties')}
                            </p>
                            <ul className="text-xs text-gray-500 space-y-0.5">
                              {uniqueTps.map((tp) => {
                                const domainLower = tp.domain.toLowerCase();
                                // Flag any domain that isn't disclosed in the DSE, regardless
                                // of which consent column we're in — the label "fehlt in DSE"
                                // is the accurate concern in every state. Technically-necessary
                                // static-asset CDNs are excluded from this flag (they don't
                                // belong in a privacy policy and don't deduct from the score).
                                const isNotInDse = !dseTpDomains.has(domainLower) && !technicallyNecessaryTpDomains.has(domainLower);
                                return (
                                  <li
                                    key={`${key}-tp-${domainLower}`}
                                    className={`font-mono truncate ${isNotInDse ? 'text-red-700' : ''}`}
                                  >
                                    {tp.domain}
                                    {isNotInDse && (
                                      <span className="ml-1 text-[10px] px-1 py-0.5 rounded border border-red-200 bg-red-50 text-red-700 align-middle">
                                        {t('route.scanresult.details.missing_in_dse')}
                                      </span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}

export const ScanResultDisplay = memo(ScanResultDisplayComponent);
ScanResultDisplay.displayName = 'ScanResultDisplay';
